import apiClient from './client';

export interface Post {
  id: number;
  title: string;
  content: string;
  user_id: number;
  author_username: string;
  author_display_name: string;
  author_avatar_url: string;
  author_first_name?: string;
  author_last_name?: string;
  status: string;
  category: string;
  tags: string[];
  likes_count: number;
  comments_count: number;
  created_at: string;
  updated_at: string;
}

export interface Comment {
  id: number;
  content: string;
  user_id: number;
  post_id: number;
  parent_id: number | null;
  author_username: string;
  author_display_name: string;
  author_avatar_url: string;
  author_first_name?: string;
  author_last_name?: string;
  likes_count: number;
  created_at: string;
  updated_at: string;
  replies?: Comment[];
}

export interface PostCreateData {
  title: string;
  content: string;
  category?: string;
  tags?: string[];
  status?: string;
}

export interface CommentCreateData {
  content: string;
  postId: number;
  parentId?: number | null;
}

export interface PostQueryParams {
  limit?: number;
  offset?: number;
  status?: string;
  category?: string;
  tags?: string;
  userId?: number;
}

export const discussionService = {
  // Post operations
  getPosts: (params?: PostQueryParams) => 
    apiClient.get<{posts: Post[], pagination: any}>('discussions/posts', { params }),
  
  getPostById: (id: number) => 
    apiClient.get<{post: Post, userHasLiked: boolean}>(`discussions/posts/${id}`),
  
  createPost: (postData: PostCreateData) => 
    apiClient.post<{message: string, post: Post}>('discussions/posts', postData),
  
  updatePost: (id: number, postData: Partial<PostCreateData>) => 
    apiClient.put<{message: string, post: Post}>(`discussions/posts/${id}`, postData),
  
  deletePost: (id: number) => 
    apiClient.delete<{message: string}>(`discussions/posts/${id}`),
  
  likePost: (id: number) => 
    apiClient.post<{message: string}>(`discussions/posts/${id}/like`),
  
  unlikePost: (id: number) => 
    apiClient.delete<{message: string}>(`discussions/posts/${id}/like`),
  
  getPostLikes: (id: number) => 
    apiClient.get<{likes: any[], count: number}>(`discussions/posts/${id}/likes`),
  
  // Comment operations
  getCommentById: (id: number) => 
    apiClient.get<{comment: Comment, replies: Comment[], userHasLiked: boolean}>(`discussions/comments/${id}`),
  
  getPostComments: (postId: number, limit?: number, offset?: number) => 
    apiClient.get<{comments: Comment[], count: number}>(`discussions/posts/${postId}/comments`, {
      params: { limit, offset }
    }),
  
  createComment: async (commentData: CommentCreateData) => {
    // Create a clean request object with proper typing including postId
    const requestData: { 
      content: string; 
      postId: number;
      parent_id?: number | null;
    } = {
      content: commentData.content,
      postId: commentData.postId
    };
    
    // Only add parent_id if it actually has a value
    if (commentData.parentId) {
      requestData.parent_id = commentData.parentId;
    }
    
    console.log('Sending comment with format:', JSON.stringify(requestData));
    
    // Use a simple approach with standard Axios
    return apiClient.post<{message: string, comment: Comment}>(
      `discussions/posts/${commentData.postId}/comments`, 
      requestData
    );
  },
  
  updateComment: (id: number, content: string) => 
    apiClient.put<{message: string, comment: Comment}>(`discussions/comments/${id}`, { content }),
  
  deleteComment: (id: number) => 
    apiClient.delete<{message: string}>(`discussions/comments/${id}`),
  
  likeComment: (id: number) => 
    apiClient.post<{message: string}>(`discussions/comments/${id}/like`),
  
  unlikeComment: (id: number) => 
    apiClient.delete<{message: string}>(`discussions/comments/${id}/like`),
  
  getCommentLikes: (id: number) => 
    apiClient.get<{likes: any[], count: number}>(`discussions/comments/${id}/likes`),
}; 